import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token') || null)
  const loading = ref(false)
  
  // Computed properties
  const isAuthenticated = computed(() => authService.isAuthenticated())
  
  // Inicializar usuario desde localStorage
  const initUser = () => {
    const userData = authService.getCurrentUser()
    if (userData) {
      user.value = userData
    }
  }
  
  // Inicializar al crear el store
  initUser()
  
  // Acciones
  const login = async (credentials) => {
    loading.value = true
    try {
      const response = await authService.login(credentials)
      
      if (response.success) {
        user.value = response.data.user
        token.value = response.data.access_token

        // Guardar permisos instantáneamente si vienen en la respuesta
        if (response.data.permissions_info) {
           const { usePermissionsStore } = await import('@/stores/permissions')
           const permissionsStore = usePermissionsStore()
           permissionsStore.setPermissionsInfo(response.data.permissions_info)
        }
        
        return {
          success: true,
          user: response.data.user,
          message: response.message
        }
      } else {
        return {
          success: false,
          message: response.message,
          errors: response.errors || {},
          error_type: response.error_type || null,
          account_status: response.account_status || null
        }
      }
    } catch (error) {
      console.error('💥 STORE: Error en login:', error)
      return {
        success: false,
        message: 'Error interno del sistema'
      }
    } finally {
      loading.value = false
    }
  }
  
  const register = async (userData) => {
    loading.value = true
    try {
      const response = await authService.register(userData)
      
      if (response.success) {
        // El nuevo flujo NO devuelve token inmediatamente
        // El usuario debe verificar email y esperar aprobación
        // NO guardamos user ni token aquí
        
        return {
          success: true,
          data: response.data,
          message: response.message
        }
      } else {
        return {
          success: false,
          message: response.message,
          errors: response.errors || {}
        }
      }
    } catch (error) {
      console.error('Register store error:', error)
      return {
        success: false,
        message: 'Error interno del sistema'
      }
    } finally {
      loading.value = false
    }
  }
  
  const logout = async () => {
    loading.value = true
    try {
      await authService.logout()
    } finally {
      // Limpiar permisos
      try {
        const { usePermissionsStore } = await import('@/stores/permissions')
        const permissionsStore = usePermissionsStore()
        permissionsStore.clearPermissions()
      } catch (e) {
        console.error('Error clearing permissions:', e)
      }

      user.value = null
      token.value = null
      loading.value = false
      
      // Redirigir al login después del logout
      window.location.href = '/login'
    }
  }
  
  const updateProfile = async () => {
    try {
      const response = await authService.getProfile()
      if (response.success) {
        user.value = response.data
      }
      return response
    } catch (error) {
      console.error('Update profile error:', error)
      return {
        success: false,
        message: 'Error al actualizar perfil'
      }
    }
  }
  
  return {
    // State
    user,
    token,
    loading,
    
    // Getters
    isAuthenticated,
    
    // Actions
    login,
    register,
    logout,
    updateProfile,
    initUser
  }
})